'use client'

import React, { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Laugh, Mail, Phone } from 'lucide-react'

import { useSession } from '@rewardops/auth'
import { Divider, LocalizedLink } from '@rewardops/ui'
import { cn } from '@rewardops/ui/src/lib/utils'
import { Footer, localeFromLocaleStr } from '@rewardops/utils'

import { useTranslation } from '~/app/i18n/client'
import { useApi } from '../hooks/use-api'
import ResponsiveComponent from '../responsive-component'
import { PointsAdjustmentFormModal } from './components/forms/points-adjustment-form'
import MapleLogo from './maple-logo'

interface MapleFooterProps {
  localeStr: string
  className?: string
}

type LegalLink = {
  name: string
  id: string
  to: string
  isRelativeUrl: boolean
}

const MapleFooter: React.FC<MapleFooterProps> = ({
  className,
  localeStr,
}: MapleFooterProps) => {
  const { api, tenant } = useApi()
  const locale = localeFromLocaleStr(localeStr)
  const { data: session } = useSession()
  const { t: translate } = useTranslation(undefined, undefined, locale)
  const [links, setLinks] = useState<LegalLink[] | undefined>([])

  const pathname = `${usePathname()}/`?.split(locale?.locale)
  const currentPage = pathname[pathname?.length - 1] ?? ''

  const { data }: { data: Footer | undefined } =
    api.contentful.getById.useQuery({
      key: 'footer',
      type: 'footer',
      language: localeStr,
    })

  useEffect(() => {
    if (!session?.user) {
      const legalLinks = data?.legalLinks?.filter(
        (link) => link?.id !== 'points-inquiry',
      )

      setLinks(legalLinks)
    }

    if (session?.user) {
      setLinks(data?.legalLinks)
    }
  }, [data, session])

  const pageFootnote = data?.footNotes?.find((note) =>
    currentPage?.includes(note?.pagePath),
  )

  const footerInfo = (
    <div className="mb-8 flex flex-col gap-6">
      {data?.phoneNumber && (
        <div
          className="flex items-center gap-2 text-sm"
          style={{ color: data?.fontColor }}
        >
          <Phone aria-label="phone" width={16} height={16} />
          {data?.phoneNumber}
        </div>
      )}

      {data?.email && (
        <div
          className="flex items-center gap-2 text-sm"
          style={{ color: data?.fontColor }}
        >
          <Mail aria-label="email" width={16} height={16} />
          {data?.email}
        </div>
      )}

      {data?.helperMessage && (
        <div
          className="flex items-center gap-2 text-sm"
          style={{ color: data?.fontColor }}
        >
          <Laugh aria-label="laugh" width={16} height={16} />
          {data?.helperMessage}
        </div>
      )}
    </div>
  )

  const renderLegalLink = (link: LegalLink) => {
    if (link.id === 'points-inquiry') {
      if (!tenant.flags.retailersForm) return
      return (
        <PointsAdjustmentFormModal>
          <button>{link?.name}</button>
        </PointsAdjustmentFormModal>
      )
    }

    if (!link?.isRelativeUrl) {
      return (
        <a href={link?.to} target="_blank" rel="noopener noreferrer">
          {link?.name}
        </a>
      )
    }

    return <LocalizedLink href={link?.to}>{link?.name}</LocalizedLink>
  }

  const copyrightText = translate('footer.copyright')
  const Copyright = () => (
    <p className="text-sm" style={{ color: data?.fontColor }}>
      {copyrightText === 'footer.copyright' ? (
        <>
          Copyright © {new Date().getFullYear()}, RewardOps Inc.{' '}
          {translate('footer.allRightsReserved')}
        </>
      ) : (
        copyrightText
      )}
    </p>
  )

  const cookieManagementLink = tenant?.flags?.cookieManagement && (
    <div
      className="text-sm font-bold"
      style={{ color: data?.legalLinkFontColor }}
    >
      <LocalizedLink
        href="#"
        onClick={() => window.OneTrust.ToggleInfoDisplay()}
      >
        {translate('footer.manageOnlineExperience')}
      </LocalizedLink>
    </div>
  )

  const desktopComponent = (
    <footer
      className={cn('min-h-[314px] px-36 py-8', className)}
      style={{ backgroundColor: data?.backgroundColor }}
    >
      <div className="mb-8 flex items-center">
        {data?.logoToggle && (
          <div className="mr-9">
            <MapleLogo />
          </div>
        )}
        <div className="flex gap-6">
          {links?.map((link) => (
            <div
              key={link?.id}
              className="text-sm font-bold"
              style={{ color: data?.legalLinkFontColor }}
            >
              {renderLegalLink(link)}
            </div>
          ))}
          {cookieManagementLink}
        </div>
      </div>
      <Divider className="mb-8 bg-gray-300" />
      {footerInfo}
      {data?.copyrightToggle && (
        <>
          <Divider className="mb-8 bg-gray-300" />
          <Copyright />
        </>
      )}
      {pageFootnote?.footnote ? (
        <div className="mt-8 text-sm" style={{ color: data?.fontColor }}>
          {pageFootnote?.footnote}
        </div>
      ) : null}
    </footer>
  )

  const mobileComponent = (
    <footer
      className="min-h-[514px] p-6"
      style={{ backgroundColor: data?.backgroundColor }}
    >
      <div>
        {data?.logoToggle && (
          <div className="mb-8">
            <MapleLogo />
          </div>
        )}
        <div className="mb-8 flex flex-wrap gap-x-6 gap-y-8">
          {links?.map((link) => (
            <div
              key={link?.id}
              className="text-sm font-bold"
              style={{ color: data?.legalLinkFontColor }}
            >
              {renderLegalLink(link)}
            </div>
          ))}
          {cookieManagementLink}
        </div>
        <Divider className="mb-8 bg-gray-300" />
        {footerInfo}
        {data?.copyrightToggle && (
          <>
            <Divider className="mb-8 bg-gray-300" />
            <Copyright />
          </>
        )}
        {pageFootnote?.footnote ? (
          <div className="mt-8 text-sm" style={{ color: data?.fontColor }}>
            {pageFootnote?.footnote}
          </div>
        ) : null}
      </div>
    </footer>
  )

  return (
    <ResponsiveComponent
      desktopComponent={desktopComponent}
      mobileComponent={mobileComponent}
    />
  )
}

export { MapleFooter }
